/**
 * Le live au grand écran en Concentré (Wajih, 19/10) : l'activité prend tout l'écran, l'énoncé se lit
 * sur une colonne centrée, et le panneau de Sam s'ouvre à droite en décalant l'énoncé. Deux règles
 * pures, sur la largeur de l'écran.
 *
 * La colonne est celle d'un **énoncé de maths, pas d'un roman** (Wajih, 20/10 : « 522 px, trop
 * étroit ») : des phrases courtes, des formules, des questions numérotées et des figures. Mesurée sur
 * l'énoncé long des stories (`studentActivityLongStatementMock`, trois parties, une figure), à 680 px :
 *  - une ligne tient environ 88 caractères depuis que l'énoncé est en PT Serif (95 du temps d'Outfit,
 *    où la colonne a été mesurée), au haut de ce qui se lit d'un trait, parce qu'une formule en ligne
 *    prend plus de place que ses caractères ;
 *  - une figure, que le `<RichText>` plafonne à la moitié d'un bloc d'au moins 600 px sans la laisser
 *    sous 400 px (`figureMaxWidth`), y fait 400 px : un repère y montre ses carreaux ;
 *  - l'énoncé raccourcit d'un dixième (1 199 → 1 084 px de haut), sans devenir une page de journal.
 * Et à 1 280 px, le panneau de Sam ouvert, elle tient encore en entier à côté de lui.
 */
import { SAM_PANEL_WIDTH } from "../../organisms/SamSheet/samSheetGeometry"
import { TWO_PANE_LIST_WIDTH } from "../../templates/TwoPaneTemplate/twoPaneGeometry"

/**
 * La chasse d'un caractère qui borne les colonnes : 7 px, les 6,8 px mesurés sur l'énoncé en Outfit,
 * arrondis. L'énoncé est en PT Serif depuis (environ 7,4 px : 88 caractères dans la colonne de 680) ;
 * la valeur est gardée, les largeurs ne changent pas.
 */
export const READING_CHAR_WIDTH = 7
/** La marge de l'énoncé de chaque côté de sa colonne (`spacing.4`). */
export const READING_PADDING = 16
/** La colonne de lecture d'un énoncé, marges comprises. */
const READING_COLUMN = 680
/**
 * Le plus étroit où la colonne se resserre, pour laisser le panneau de Sam à côté d'elle : environ
 * 70 caractères par ligne (la colonne d'avant le 20/10).
 */
export const READING_COLUMN_MIN = 70 * READING_CHAR_WIDTH + 2 * READING_PADDING
/** De chaque côté de la colonne, au moins cette marge avant le bord ou le panneau. */
const COLUMN_GUTTER = 16

/** La colonne de lecture, marges comprises. */
export function readingColumnWidth(): number {
    return READING_COLUMN
}

/**
 * La visio la plus étroite, quand l'activité est agrandie à côté d'elle : celle d'un téléphone, où la
 * scène tient tout (`stageLayout.test.ts`, de 296 à 366 px de large ; le duo Sam et masteur y garde sa
 * superposition).
 */
export const STAGE_MIN_WIDTH = 360

/**
 * La largeur du volet de l'activité, la visio ouverte au grand écran (Wajih, daily du 23/10, l'option (b)
 * du cadrage du 22/10) : le volet d'un écran à deux volets, ou, agrandie par le bouton rond sur la
 * séparation, la colonne de lecture — l'énoncé n'a rien à gagner au-delà (20/10). La visio garde toujours
 * `STAGE_MIN_WIDTH` ; jamais l'activité ne passe sous son volet.
 */
export function activityPaneWidth(width: number | null, enlarged: boolean): number {
    if (!enlarged || width == null) return TWO_PANE_LIST_WIDTH
    return Math.max(TWO_PANE_LIST_WIDTH, Math.min(READING_COLUMN, width - STAGE_MIN_WIDTH))
}

export type SamPanelPlacement = {
    /** Le panneau tient à côté de la colonne : pas de voile, l'activité se décale. */
    beside: boolean
    /** De combien l'activité se décale vers la gauche (la largeur du panneau), ou 0. */
    shift: number
}

/**
 * Le panneau de Sam ouvert : à côté de l'énoncé quand la colonne, resserrée au besoin jusqu'à
 * `READING_COLUMN_MIN`, tient à côté de lui dans `width` — l'énoncé décalé reste lisible en entier ;
 * sinon, par-dessus avec son voile, comme ailleurs. La colonne se resserre d'elle-même : elle n'a
 * qu'une largeur maximale, dans la place que lui laisse le décalage.
 */
export function samPanelPlacement(width: number): SamPanelPlacement {
    const beside = width >= SAM_PANEL_WIDTH + READING_COLUMN_MIN + 2 * COLUMN_GUTTER
    return { beside, shift: beside ? SAM_PANEL_WIDTH : 0 }
}
